import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const NotificationSection = () => {
  const [notifications, setNotifications] = useState({
    email: {
      inventoryAlerts: true,
      valuationUpdates: true,
      marketReports: false,
      systemUpdates: true,
      billingNotices: true,
      securityAlerts: true
    },
    sms: {
      inventoryAlerts: true,
      valuationUpdates: false,
      marketReports: false,
      systemUpdates: false,
      billingNotices: true,
      securityAlerts: true
    },
    push: {
      inventoryAlerts: false,
      valuationUpdates: false,
      marketReports: false,
      systemUpdates: true,
      billingNotices: false,
      securityAlerts: true
    }
  });

  const [alertSettings, setAlertSettings] = useState({
    priceDropThreshold: 15,
    newListingsRadius: 250,
    alertFrequency: 'immediate',
    quietHours: {
      enabled: true,
      start: '22:00',
      end: '08:00'
    }
  });

  const notificationTypes = [
    {
      key: 'inventoryAlerts',
      label: 'Inventory Alerts',
      description: 'Price drops, new listings, and market changes for watched cranes'
    },
    {
      key: 'valuationUpdates',
      label: 'Valuation Updates',
      description: 'When new comparable sales affect your saved valuations'
    },
    {
      key: 'marketReports',
      label: 'Market Reports',
      description: 'Weekly market trends and industry insights'
    },
    {
      key: 'systemUpdates',
      label: 'System Updates',
      description: 'New features, maintenance notifications, and platform updates'
    },
    {
      key: 'billingNotices',
      label: 'Billing Notices',
      description: 'Payment confirmations, invoice reminders, and subscription changes'
    },
    {
      key: 'securityAlerts',
      label: 'Security Alerts',
      description: 'Login attempts, password changes, and account security events'
    }
  ];

  const handleNotificationChange = (channel, type, checked) => {
    setNotifications(prev => ({
      ...prev,
      [channel]: {
        ...prev?.[channel],
        [type]: checked
      }
    }));
  };

  const handleAlertSettingChange = (key, value) => {
    setAlertSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleQuietHoursChange = (key, value) => {
    setAlertSettings(prev => ({
      ...prev,
      quietHours: {
        ...prev?.quietHours,
        [key]: value
      }
    }));
  };

  const handleSaveSettings = () => {
    console.log('Saving notification settings:', { notifications, alertSettings });
    // Here you would typically make an API call to save the settings
  };

  const handleTestNotification = (channel) => {
    console.log(`Sending test notification via ${channel}`);
    // Here you would typically trigger a test notification
  };

  return (
    <div className="space-y-6">
      {/* Notification Preferences */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="Bell" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-text-primary">Notification Preferences</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveSettings}
            iconName="Save"
            iconPosition="left"
          >
            Save Settings
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Notification Type</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-text-secondary">
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Mail" size={16} />
                    <span>Email</span>
                  </div>
                </th>
                <th className="text-center py-3 px-4 text-sm font-medium text-text-secondary">
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="MessageSquare" size={16} />
                    <span>SMS</span>
                  </div>
                </th>
                <th className="text-center py-3 px-4 text-sm font-medium text-text-secondary">
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Smartphone" size={16} />
                    <span>Push</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {notificationTypes?.map((type) => (
                <tr key={type?.key} className="border-b border-border">
                  <td className="py-4 px-4">
                    <div>
                      <h4 className="text-sm font-medium text-text-primary">{type?.label}</h4>
                      <p className="text-xs text-text-secondary mt-1">{type?.description}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <Checkbox
                      checked={notifications?.email?.[type?.key]}
                      onChange={(e) => handleNotificationChange('email', type?.key, e?.target?.checked)}
                    />
                  </td>
                  <td className="py-4 px-4 text-center">
                    <Checkbox
                      checked={notifications?.sms?.[type?.key]}
                      onChange={(e) => handleNotificationChange('sms', type?.key, e?.target?.checked)}
                    />
                  </td>
                  <td className="py-4 px-4 text-center">
                    <Checkbox
                      checked={notifications?.push?.[type?.key]}
                      onChange={(e) => handleNotificationChange('push', type?.key, e?.target?.checked)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Alert Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Settings" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">Alert Settings</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Price Drop Threshold
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="5"
                  value={alertSettings?.priceDropThreshold}
                  onChange={(e) => handleAlertSettingChange('priceDropThreshold', parseInt(e?.target?.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium text-text-primary w-12">
                  {alertSettings?.priceDropThreshold}%
                </span>
              </div>
              <p className="text-xs text-text-secondary mt-1">
                Get alerts when crane prices drop by this percentage
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                New Listings Radius
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="range"
                  min="50"
                  max="500"
                  step="25"
                  value={alertSettings?.newListingsRadius}
                  onChange={(e) => handleAlertSettingChange('newListingsRadius', parseInt(e?.target?.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium text-text-primary w-16">
                  {alertSettings?.newListingsRadius} mi
                </span>
              </div>
              <p className="text-xs text-text-secondary mt-1">
                Monitor new crane listings within this radius
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Alert Frequency
              </label>
              <select
                value={alertSettings?.alertFrequency}
                onChange={(e) => handleAlertSettingChange('alertFrequency', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="immediate">Immediate</option>
                <option value="hourly">Hourly Digest</option>
                <option value="daily">Daily Digest</option>
                <option value="weekly">Weekly Summary</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-text-primary">
                  Quiet Hours
                </label>
                <Checkbox
                  checked={alertSettings?.quietHours?.enabled}
                  onChange={(e) => handleQuietHoursChange('enabled', e?.target?.checked)}
                />
              </div>
              
              {alertSettings?.quietHours?.enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">From</label>
                    <input
                      type="time"
                      value={alertSettings?.quietHours?.start}
                      onChange={(e) => handleQuietHoursChange('start', e?.target?.value)}
                      className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">To</label>
                    <input
                      type="time"
                      value={alertSettings?.quietHours?.end}
                      onChange={(e) => handleQuietHoursChange('end', e?.target?.value)}
                      className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Test Notifications */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="TestTube" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">Test Notifications</h3>
        </div>
        
        <p className="text-sm text-text-secondary mb-4">
          Send test notifications to verify your settings are working correctly.
        </p>
        
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTestNotification('email')}
            iconName="Mail"
            iconPosition="left"
          >
            Test Email
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTestNotification('sms')}
            iconName="MessageSquare"
            iconPosition="left"
          >
            Test SMS
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTestNotification('push')}
            iconName="Smartphone"
            iconPosition="left"
          >
            Test Push
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSection;